const SORT_KEY_MAP = {
  reportingMonth: "reportingDate",
  entryDate: "entryDate",
  label: "label",
  value: "value",
  unit: "unit",
};

export const sortEntries = (entries, sort) => {
  if (!sort?.key) return entries;
  const dataKey = SORT_KEY_MAP[sort.key] || sort.key;
  return [...entries].sort((a, b) => {
    let aVal = a[dataKey] ?? "";
    let bVal = b[dataKey] ?? "";
    if (dataKey === "value") {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sort.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sort.direction === "asc" ? 1 : -1;
    return 0;
  });
};

export const formatEntryDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const toFiniteNumber = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

// Unified numeric formatter for all metric values across Data Entry,
// Overview, Consolidated, and NxMap dialogs. 4-decimal maximum,
// trailing zeros stripped, thousands separators per the locale.
// toLocaleString's maximumFractionDigits handles both behaviours natively.
export const formatMetricValue = (value) => {
  if (value == null || value === "") return "n.a";
  const n = toFiniteNumber(value);
  if (n == null) return String(value);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatTotal = (totalValue, baseUnit) => {
  if (totalValue == null) return null;
  const formatted = formatMetricValue(totalValue);
  return baseUnit ? `${formatted} ${baseUnit}` : formatted;
};

// CO2e figure derived from a year group's raw total, independent of the
// metric's own unit (MJ, kg, etc.) — always expressed in kgCO₂e.
export const formatCarbonEmissionReduction = (totalValue, multiplier) => {
  if (totalValue == null || multiplier == null) return null;
  const total = toFiniteNumber(totalValue);
  if (total == null) return null;
  return `${formatMetricValue(total * multiplier)} kgCO₂e`;
};

export const formatEntryValue = (value, unit) => {
  if (!value) return "";
  return unit ? `${value} ${unit}` : value;
};

export const filterOrgEntries = (
  dataEntries = {},
  orgMap,
  { selectedOrg = [], selectedYear = "all", searchEntry = "" } = {},
  mapYearData,
  orgLookupMap,
) => {
  const search = searchEntry.trim().toLowerCase();

  return Object.entries(dataEntries)
    .filter(
      ([orgId]) => selectedOrg.length === 0 || selectedOrg.includes(orgId),
    )
    .map(([orgId, yearGroups]) => {
      const org = orgMap.get(orgId);
      // Fallback: orgs missing from orgMap (e.g. soft-archived isActive:false)
      // resolve via the lookup map so the row header shows the org name
      // instead of the raw ObjectId.
      const fallback = !org ? orgLookupMap?.get(orgId) : null;
      const breadcrumb = org?.pathLabel ? org.pathLabel.split(" > ") : [];
      const orgName = org?.name || fallback?.name || orgId;
      const isArchived = !org && fallback && fallback.isActive === false;

      const years = Object.entries(yearGroups)
        .filter(([year]) => selectedYear === "all" || year === selectedYear)
        .map(([year, yearData]) => mapYearData(year, yearData, search))
        .filter(Boolean)
        .sort((a, b) => b.year - a.year);

      return {
        orgId,
        orgName,
        isArchived,
        breadcrumb,
        years,
      };
    })
    .filter((org) => org.years.length > 0);
};

const formatParentLabel = (value) => {
  if (!value || value === "Custom Disclosures") return value;
  return String(value).replaceAll("_", " ");
};

export const getGroupedDisclosures = (disclosures = []) => {
  const groups = new Map();
  const seenCodesByGroup = new Map();

  for (const d of disclosures) {
    const isSingle = !d.isCustom && !d.parentCode;
    // Custom disclosures group by their (required) Grouping Topic, same role
    // parentCode plays for standard ones. Legacy customs saved before topic
    // was required fall back to one shared "Custom Disclosures" bucket.
    const customLabel = d.isCustom ? d.topic || "Custom Disclosures" : null;
    const key = d.isCustom ? `custom:${customLabel}` : d.parentCode || d.code;

    let group = groups.get(key);
    if (!group) {
      group = {
        // Unique per group — parentCode alone collides when a custom
        // framework topic shares its name with a standard parent group.
        groupKey: key,
        parentCode: d.isCustom ? customLabel : d.parentCode || d.code,
        parentTitle: d.isCustom ? customLabel : d.parentTitle || d.title,
        isSingle,
        children: [],
      };
      group.parentLabel = formatParentLabel(group.parentCode);
      groups.set(key, group);
      seenCodesByGroup.set(key, new Set());
    }

    const seenCodes = seenCodesByGroup.get(key);
    if (!seenCodes.has(d.code)) {
      seenCodes.add(d.code);
      group.children.push(d);
    }
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      children: group.children
        .slice()
        .sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    }))
    .sort((a, b) => (a.parentLabel || "").localeCompare(b.parentLabel || ""));
};
